import { Receipt, CreditCard, Users, Settings } from 'lucide-react';
import { useModules } from '@/hooks/use-modules';
import { useLocationStore } from '@/stores/location-store';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * The home page for roles that can't see business analytics (manager and
 * below). Preserves the shortcut grid the page carried before the analytics
 * rebuild — without it those users would land on an empty page.
 */
export function QuickLinks() {
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData } = useModules(selectedLocationId);
  const activeModules = (modulesData?.data ?? []).filter((m) => m.isActive);

  // A link shows only when its tab opens for this user.
  const permissions = usePermissions();

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Quick Links</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {activeModules.some((m) => m.moduleName === 'collections') && permissions.canOpen('receivePayments') && (
          <a
            href="#/receive-payments"
            className="flex items-center gap-3 rounded-xl border border-border bg-white p-4 transition-colors hover:bg-muted"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Receive Payments</p>
              <p className="text-xs text-muted-foreground">Manage Invoices</p>
            </div>
          </a>
        )}
        {activeModules.some((m) => m.moduleName === 'payables') && permissions.canOpen('sendPayments') && (
          <a
            href="#/send-payments"
            className="flex items-center gap-3 rounded-xl border border-border bg-white p-4 transition-colors hover:bg-muted"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Send Payments</p>
              <p className="text-xs text-muted-foreground">Manage Vendor Payments</p>
            </div>
          </a>
        )}
        {permissions.canOpen('users') && (
          <a
            href="#/users"
            className="flex items-center gap-3 rounded-xl border border-border bg-white p-4 transition-colors hover:bg-muted"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Users</p>
              <p className="text-xs text-muted-foreground">Manage Team</p>
            </div>
          </a>
        )}
        {permissions.atLeast('admin') && (
          <a
            href="#/settings"
            className="flex items-center gap-3 rounded-xl border border-border bg-white p-4 transition-colors hover:bg-muted"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-foreground">Settings</p>
              <p className="text-xs text-muted-foreground">Configure Modules</p>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}
